import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Receipt,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  User,
  Phone,
  Calendar,
  Hash,
  FileText,
  Printer
} from 'lucide-react';
import { exchangesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import { printExchangeReceipt } from '../../utils/pdf';

const ExchangeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();

  const [exchange, setExchange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  

  useEffect(() => {
    loadExchange();
  }, [id]);

  const loadExchange = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await exchangesAPI.get(id);
      setExchange(response.data);
    } catch (error) {
      console.error('Error loading exchange:', error);
      setError('Failed to load exchange details');
      addNotification('Failed to load exchange details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    await printExchangeReceipt(exchange);
  };

  const handleRetrySync = async () => {
    try {
      setRetrying(true);
      await exchangesAPI.retrySync(id);
      addNotification({ type: 'success', message: 'WC sync retry initiated' });
      loadExchange();
    } catch (error) {
      addNotification({ type: 'error', message: error.response?.data?.detail || 'Failed to retry sync' });
    } finally {
      setRetrying(false);
    }
  };

  const getExchangeStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DELETED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExchangeDifferenceColor = (difference) => {
    if (difference > 0) {
      return 'text-red-600'; // Customer pays extra
    } else if (difference < 0) {
      return 'text-green-600'; // Store pays back
    } else {
      return 'text-gray-600'; // Even exchange
    }
  };

  const getExchangeDifferenceText = (difference) => {
    if (difference > 0) {
      return `Customer pays ${formatPrice(difference)}`;
    } else if (difference < 0) {
      return `Store pays ${formatPrice(Math.abs(difference))}`;
    } else {
      return 'Even exchange';
    }
  };

  const getWcSyncStatusStyle = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'synced': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading exchange details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Details</h1>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!exchange) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Details</h1>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Exchange not found</p>
        </div>
      </div>
    );
  }

  const returnedItems = exchange.details?.filter(d => d.ExchangeType === 'RETURNED') || [];
  const givenItems = exchange.details?.filter(d => d.ExchangeType === 'GIVEN') || [];

  const wcOrders = exchange.woocommerce_orders || [];
  const woocommerceId = wcOrders.length
    ? wcOrders
        .map((o) => (wcOrders.length > 1 ? `${o.woocommerce_id} (${o.brand})` : `${o.woocommerce_id}`))
        .join(', ')
    : '-';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Details</h1>
        </div>
        
        <div className="flex items-center space-x-3">
        <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Exchange Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Exchange Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Hash className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Exchange Number</div>
                    <div className="font-medium text-gray-900">
                      {exchange.ExchangeNo || `EX-${exchange.ID}`}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Exchange Date</div>
                    <div className="font-medium text-gray-900">
                      {dayjs(exchange.ExchangeDate).format('MMM DD, YYYY HH:mm')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Receipt className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">WooCommerce Order ID</div>
                    <div className="font-medium text-gray-900">{woocommerceId}</div>
                  </div>
                </div>

              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Customer Name</div>
                    <div className="font-medium text-gray-900">
                      {exchange.CustomerName || 'Counter Exchange'}
                    </div>
                  </div>
                </div>

                {exchange.CustomerPhone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-500">Customer Phone</div>
                      <div className="font-medium text-gray-900">
                        {exchange.CustomerPhone}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Status</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getExchangeStatusColor(exchange.Status)}`}>
                      {exchange.Status || 'ACTIVE'}
                    </span>
                  </div>
                </div>

                {exchange.wc_sync_status && (
                  <div className="flex items-center space-x-3">
                    <RefreshCw className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-500">WC Sync Status</div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWcSyncStatusStyle(exchange.wc_sync_status)}`}>
                          {exchange.wc_sync_status}
                        </span>
                        {exchange.wc_sync_status === 'failed' && (
                          <button
                            onClick={handleRetrySync}
                            disabled={retrying}
                            className="text-sm px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {retrying ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {exchange.ExchangeRemarks && (
              <div className="mt-6">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Exchange Remarks</div>
                    <div className="text-gray-900">{exchange.ExchangeRemarks}</div>
                  </div>
                </div>
              </div>
            )}

            {exchange.Remarks && (
              <div className="mt-4">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Additional Remarks</div>
                    <div className="text-gray-900">{exchange.Remarks}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Returned Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Returned Items</h2>
            
            {returnedItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {returnedItems.map((item) => (
                      <tr key={item.ID}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{item.item?.Name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPrice(item.Price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPrice(item.Discount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatPrice((item.Quantity * item.Price) - (item.Discount * item.Quantity))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No returned items
              </div>
            )}
          </div>

          {/* Sold Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sold Items</h2>
            
            {givenItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {givenItems.map((item) => (
                      <tr key={item.ID}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{item.item?.Name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPrice(item.Price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPrice(item.Discount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatPrice((item.Quantity * item.Price) - item.Discount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No Sold Items
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Exchange Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Exchange Summary</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Returned Items Total:</span>
                <span className="font-medium">{formatPrice(exchange.TotalReturnValue)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Sold Items Total:</span>
                <span className="font-medium">{formatPrice(exchange.TotalGivenValue)}</span>
              </div>
              
              {/* Net Totals after discounts */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Net Totals (After Discounts)</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Returned Total:</span>
                    <span className="font-medium text-blue-600">
                      {formatPrice((exchange.TotalReturnValue || 0) - (exchange.ReturnedItemsDiscountAmount || 0))}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Given Total:</span>
                    <span className="font-medium text-green-600">
                      {formatPrice((exchange.TotalGivenValue || 0) - (exchange.GivenItemsDiscountAmount || 0) - (exchange.Discount || 0))}
                    </span>
                  </div>
                </div>
              </div>

              {/* Discount Information */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Discount Information</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Returned Items Discount:</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{exchange.ReturnedItemsDiscountPercentage || 0}%</div>
                      <div className="font-medium text-blue-600">-{formatPrice(exchange.ReturnedItemsDiscountAmount || 0)}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Given Items Discount:</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{exchange.GivenItemsDiscountPercentage || 0}%</div>
                      <div className="font-medium text-green-600">-{formatPrice(exchange.GivenItemsDiscountAmount || 0)}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Additional Discount:</span>
                    <span className="font-medium">-{formatPrice(exchange.Discount || 0)}</span>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Exchange Difference:</span>
                  <span className={`font-medium ${getExchangeDifferenceColor(exchange.ExchangeDifference)}`}>
                    {getExchangeDifferenceText(exchange.ExchangeDifference)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Cash Received:</span>
                <span className="font-medium">{formatPrice(exchange.CashReceived)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Cash Returned:</span>
                <span className="font-medium">{formatPrice(exchange.CashReturned)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Bank Received:</span>
                <span className="font-medium">{formatPrice(exchange.BankReceived)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Bank Returned:</span>
                <span className="font-medium">{formatPrice(exchange.BankReturned)}</span>
              </div>
              
            </div>
          </div>

          {/* Exchange Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Exchange Details</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Created:</span>
                <span className="text-gray-900">
                  {dayjs(exchange.CreatedDate).format('MMM DD, YYYY HH:mm')}
                </span>
              </div>
              
              {exchange.UpdatedDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="text-gray-900">
                    {dayjs(exchange.UpdatedDate).format('MMM DD, YYYY HH:mm')}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Created By:</span>
                <span className="text-gray-900">User: {exchange.CreatedBy == 1 ? 'Admin' : exchange.CreatedBy == 2 ? 'Manager' : 'Counter'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeDetail;
